import {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {Alert} from "antd";
import {Loading, PageHeader, ProjectToolNav} from "~/components";
import {useCan} from "~/hooks";
import {useGetGaConnectionQuery} from "~/reduxs/api/gaApiSlice";
import GaConnect from "../components/GaConnect";
import GaPropertyPicker from "../components/GaPropertyPicker";
import GaReports from "../components/GaReports";

/**
 * Trang tool Google Analytics 4 của một dự án (`/projects/:id/analytics`) — mirror GscHome.
 *
 * Điều hướng theo trạng thái kết nối:
 *  - chưa kết nối / revoked → GaConnect (OAuth, credential dùng chung gsc_accounts).
 *  - property_pending       → GaPropertyPicker (chọn property GA4).
 *  - connected + property   → GaReports. Poll khi còn backfill.
 */
function GaHome() {

	const {id} = useParams();
	const canGa = useCan('ga');

	const [poll, setPoll] = useState(0);
	const {data: conn, isLoading} = useGetGaConnectionQuery(id, {
		pollingInterval: poll,
		skipPollingIfUnfocused: true,
	});

	// Đang backfill → poll để cập nhật tiến độ.
	const syncing = conn?.connected && conn?.status === 'connected' && conn?.backfill_done === false;

	useEffect(() => {
		setPoll(syncing ? 15000 : 0);
	}, [syncing]);

	if (!canGa) return <div className="container">Bạn không có quyền sử dụng Google Analytics.</div>;
	if (isLoading) return <Loading />;

	const status = conn?.connected ? conn.status : 'none';

	return (
		<div className="container">
			<ProjectToolNav projectId={id} active="analytics" />

			<PageHeader
				icon="fa-light fa-chart-mixed"
				title="Google Analytics 4"
				subtitle={conn?.property_name || conn?.property || 'Lưu lượng truy cập thật từ Google Analytics'}
			/>

			{(status === 'none' || status === 'revoked') && (
				<GaConnect projectId={id} revoked={status === 'revoked'} email={conn?.email} />
			)}

			{status === 'property_pending' && (
				<GaPropertyPicker projectId={id} email={conn?.email} />
			)}

			{/* Đồng bộ bị tạm dừng vì gói không hỗ trợ (hạ gói) — dữ liệu đã có vẫn xem được. */}
			{status === 'paused' && (
				<>
					<Alert type="warning" showIcon style={{marginBottom: 16}}
						message="Đồng bộ đang tạm dừng"
						description={conn?.error_message || 'Gói hiện tại không hỗ trợ kết nối Google — đồng bộ nền đã tạm dừng, dữ liệu đã có vẫn được giữ. Nâng cấp gói để tự nối lại.'} />
					{conn?.property && <GaReports projectId={id} connection={conn} />}
				</>
			)}

			{status === 'connected' && conn?.property && (
				<GaReports projectId={id} connection={conn} />
			)}
		</div>
	);
}

export default GaHome;
